/**
 * DashboardMap — satellite-streets Mapbox map showing every project as a pin.
 *
 * Renders a single GeoJSON source with clustering enabled, three layers
 * (cluster circles, cluster counts, individual pins), and a separate "badge"
 * layer that adds a small red/amber dot to pins with active alarms.
 *
 * Click handlers:
 *   - cluster → zoom in via getClusterExpansionZoom
 *   - pin → onPinClick(siteId)
 *   - any blank map click while placeMode is set → onPlaceLocation(lng, lat)
 *
 * Hover sync:
 *   parent passes hoveredId → we set feature-state hover=true on the
 *   matching feature so the pin stroke thickens. mouseenter/leave on the
 *   pin layer also calls onHoverChange so the sidebar can highlight rows.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN } from '../../services/mapboxToken';
import type { SiteListItem } from '../../services/projectApi';
import { useAuthStore } from '../../stores/authStore';
import './DashboardMap.css';

mapboxgl.accessToken = MAPBOX_TOKEN;

export interface AlarmCount {
  critical: number;
  warning: number;
}

interface DashboardMapProps {
  projects: SiteListItem[];                          // all projects (we filter to those with coords internally)
  alarmCounts: Record<string, AlarmCount>;
  hoveredId: string | null;
  onPinClick: (siteId: string) => void;
  onHoverChange: (siteId: string | null) => void;
  placeMode: { siteId: string; siteName: string } | null;
  onPlaceLocation: (siteId: string, lng: number, lat: number) => void;
  onViewSaved?: () => void;
  onViewCleared?: () => void;
  /** Changes when the parent narrows/widens the pin set (admin sidebar:
   *  a client opened or closed). On change — never on mount — the camera
   *  re-frames: fit to the current pins, or fly back to the saved view when
   *  the focus is cleared and one exists. */
  focusKey?: string | null;
}

/* ── Saved-view helpers ────────────────────────────────────────────
   The dashboard's initial map view is a per-user-per-device preference:
   we persist it to localStorage so the user lands on their preferred
   region/zoom on next launch. Falling back to fitBounds-to-all-pins
   when no saved view exists keeps brand-new users useful.

   The key is suffixed with the logged-in user id — localStorage is
   shared by every account used in this browser profile, and the
   original unsuffixed key made one account's "Save view" leak into
   every other account (admin ↔ impersonated client). The legacy key
   is deleted on sight, never migrated: there's no telling whose view
   it holds. */

const DASHBOARD_VIEW_KEY_BASE = 'soleadlab.dashboard.defaultView';

function dashboardViewKey(): string {
  const userId = useAuthStore.getState().user?.id;
  return userId ? `${DASHBOARD_VIEW_KEY_BASE}.${userId}` : DASHBOARD_VIEW_KEY_BASE;
}

function dropLegacyUnscopedView(): void {
  try {
    localStorage.removeItem(DASHBOARD_VIEW_KEY_BASE);
  } catch { /* ignore */ }
}

interface SavedDashboardView {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
}

function loadSavedView(): SavedDashboardView | null {
  try {
    const key = dashboardViewKey();
    if (key !== DASHBOARD_VIEW_KEY_BASE) dropLegacyUnscopedView();
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedDashboardView>;
    if (
      !Array.isArray(parsed.center) ||
      parsed.center.length !== 2 ||
      typeof parsed.center[0] !== 'number' ||
      typeof parsed.center[1] !== 'number' ||
      typeof parsed.zoom !== 'number'
    ) {
      return null;
    }
    return {
      center: [parsed.center[0], parsed.center[1]],
      zoom: parsed.zoom,
      bearing: typeof parsed.bearing === 'number' ? parsed.bearing : 0,
      pitch: typeof parsed.pitch === 'number' ? parsed.pitch : 0,
    };
  } catch {
    return null;
  }
}

function persistSavedView(view: SavedDashboardView): void {
  try {
    localStorage.setItem(dashboardViewKey(), JSON.stringify(view));
  } catch {
    // localStorage can be unavailable (private mode quota); silent — the
    // notification still confirms the in-session intent, just won't survive
    // a reload.
  }
}

function clearSavedView(): void {
  try {
    localStorage.removeItem(dashboardViewKey());
  } catch { /* ignore */ }
}

function hasCoords(site: SiteListItem): site is SiteListItem & { latitude: number; longitude: number } {
  return typeof site.latitude === 'number' && typeof site.longitude === 'number';
}

function severityOf(count: AlarmCount | undefined): 'critical' | 'warning' | 'none' {
  if (!count) return 'none';
  if (count.critical > 0) return 'critical';
  if (count.warning > 0) return 'warning';
  return 'none';
}

function toFeatureCollection(
  projects: SiteListItem[],
  alarmCounts: Record<string, AlarmCount>
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const site of projects) {
    if (!hasCoords(site)) continue;
    const counts = alarmCounts[site.id];
    const severity = severityOf(counts);
    features.push({
      type: 'Feature',
      id: site.id,
      geometry: { type: 'Point', coordinates: [site.longitude, site.latitude] },
      properties: {
        id: site.id,
        name: site.name,
        severity,
        critical_count: counts?.critical ?? 0,
        warning_count: counts?.warning ?? 0,
        capacity_kw: site.capacity_kw ?? 0,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

// Style expressions kept as `unknown` and cast at use-site — mapbox-gl's
// Expression types are awkward to import and the data-driven shapes here
// are validated at runtime by the Mapbox style engine anyway.
const SEVERITY_CASE = [
  'case',
  ['==', ['get', 'severity'], 'critical'], '#DC2626',
  ['==', ['get', 'severity'], 'warning'], '#F59E0B',
  '#10B981', // none (green)
] as unknown as mapboxgl.ExpressionSpecification;

const CLUSTER_COLOR_CASE = [
  'case',
  ['>', ['get', 'critical_total'], 0], '#DC2626',
  ['>', ['get', 'warning_total'], 0], '#F59E0B',
  '#3B82F6', // calm blue when no alarms anywhere in the cluster
] as unknown as mapboxgl.ExpressionSpecification;

export default function DashboardMap({
  projects,
  alarmCounts,
  hoveredId,
  onPinClick,
  onHoverChange,
  placeMode,
  onPlaceLocation,
  onViewSaved,
  onViewCleared,
  focusKey = null,
}: DashboardMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const hoveredFeatureIdRef = useRef<string | null>(null);
  // True once the map's camera has been set from EITHER the saved view OR
  // a fitBounds across loaded pins. Either way, we don't auto-fit again.
  const didInitialFitRef = useRef(false);
  // Most-recent feature collection. Written by the data effect every render
  // so the style.load handler can seed addSource() with current data — this
  // avoids the race where data arrives before style.load fires, which would
  // otherwise leave pins invisible until the user nudges the data effect
  // (filter chip toggle, search, etc.).
  const latestDataRef = useRef<GeoJSON.FeatureCollection>({ type: 'FeatureCollection', features: [] });
  const [hasSavedView, setHasSavedView] = useState<boolean>(() => loadSavedView() !== null);
  // Imperative ref so click handlers (set once at style.load) see the current
  // place-mode without re-binding listeners on every state change.
  const placeModeRef = useRef(placeMode);
  placeModeRef.current = placeMode;
  const onPlaceLocationRef = useRef(onPlaceLocation);
  onPlaceLocationRef.current = onPlaceLocation;
  const onPinClickRef = useRef(onPinClick);
  onPinClickRef.current = onPinClick;
  const onHoverChangeRef = useRef(onHoverChange);
  onHoverChangeRef.current = onHoverChange;

  // Initialize map exactly once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const savedView = loadSavedView();
    // Always start at the globe so the dashboard opens with a sense of
    // place — then animate (flyTo) to the user's saved view, or fitBounds
    // to all their pins, once the map is ready.
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [0, 20],
      zoom: 1.2,
      bearing: 0,
      pitch: 0,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    // Cinematic intro: linger on the globe for a beat so the user registers
    // the wide-view context, then fly to their saved view. The pre-fly delay
    // intentionally outweighs the flyTo curve's ramp-up so the globe view is
    // visible on screen, not just a frame of transition.
    // We mark didInitialFitRef true ahead of the flyTo so the data-driven
    // fitBounds (in the projects effect) doesn't race the intro.
    const INTRO_HOLD_MS = 1200;
    const INTRO_FLY_MS = 2800;
    if (savedView) {
      didInitialFitRef.current = true;
      map.once('style.load', () => {
        window.setTimeout(() => {
          if (!mapRef.current) return;              // component unmounted before intro could play
          map.flyTo({
            center: savedView.center,
            zoom: savedView.zoom,
            bearing: savedView.bearing,
            pitch: savedView.pitch,
            duration: INTRO_FLY_MS,
            essential: true,                        // not skipped by prefers-reduced-motion
          });
        }, INTRO_HOLD_MS);
      });
    }

    map.on('style.load', () => {
      if (map.getSource('projects')) return;

      // Seed the source with whatever data the parent has already given us —
      // critical when projects loaded before the style finished loading.
      map.addSource('projects', {
        type: 'geojson',
        data: latestDataRef.current,
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 12,
        clusterProperties: {
          critical_total: ['+', ['case', ['==', ['get', 'severity'], 'critical'], 1, 0]],
          warning_total: ['+', ['case', ['==', ['get', 'severity'], 'warning'], 1, 0]],
        },
      });

      // Cluster bubbles
      map.addLayer({
        id: 'projects-cluster',
        type: 'circle',
        source: 'projects',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': CLUSTER_COLOR_CASE,
          'circle-radius': [
            'step', ['get', 'point_count'],
            18, 5,
            24, 20,
            30,
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      map.addLayer({
        id: 'projects-cluster-count',
        type: 'symbol',
        source: 'projects',
        filter: ['has', 'point_count'],
        layout: {
          // Explicit number-to-string conversion. `point_count_abbreviated`
          // sometimes returns undefined for clusters whose count hasn't yet
          // been aggregated by Mapbox's worker — most visible at lower zoom
          // levels where many features get merged into one cluster.
          'text-field': ['to-string', ['get', 'point_count']],
          'text-font': ['DIN Offc Pro Medium', 'Roboto Bold', 'Arial Unicode MS Bold'],
          'text-size': 14,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': 'rgba(0, 0, 0, 0.75)',
          'text-halo-width': 1.5,
        },
      });

      // Individual pins
      map.addLayer({
        id: 'projects-pin',
        type: 'circle',
        source: 'projects',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': SEVERITY_CASE,
          // Capacity-weighted radius: 6px at 100 kW, 14px at 5000 kW+.
          'circle-radius': [
            'interpolate', ['linear'], ['get', 'capacity_kw'],
            0, 7,
            100, 7,
            5000, 13,
          ],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 4,
            2,
          ],
        },
      });

      // Notification badge — small red/amber dot top-right of pins with alarms.
      map.addLayer({
        id: 'projects-pin-badge',
        type: 'circle',
        source: 'projects',
        filter: ['all',
          ['!', ['has', 'point_count']],
          ['any',
            ['>', ['get', 'critical_count'], 0],
            ['>', ['get', 'warning_count'], 0],
          ],
        ],
        paint: {
          'circle-color': [
            'case',
            ['>', ['get', 'critical_count'], 0], '#DC2626',
            '#F59E0B',
          ],
          'circle-radius': 5,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
          'circle-translate': [9, -9],
        },
      });

      // Cursor + click handlers ------------------------------------------------
      const setCursor = (cursor: string) => { map.getCanvas().style.cursor = cursor; };

      map.on('mouseenter', 'projects-pin', (e) => {
        setCursor('pointer');
        const f = e.features?.[0];
        if (!f) return;
        const id = (f.id ?? f.properties?.id) as string | undefined;
        if (!id) return;
        onHoverChangeRef.current(id);

        const props = f.properties ?? {};
        // Defensive: feature-state and the parent's projects array are both
        // sources of truth — if the source didn't preserve `name` for some
        // reason (Mapbox property serialization quirks), fall back to the
        // ref so the popup never shows a blank heading.
        const propName = String(props.name ?? '').trim();
        const fallback = latestDataRef.current.features.find(
          (feat) => (feat.id ?? feat.properties?.id) === id
        );
        const fallbackName = fallback ? String(fallback.properties?.name ?? '').trim() : '';
        const name = propName || fallbackName || 'Untitled project';
        if (!propName) {
          console.warn('[DashboardMap] hover pin missing name prop; using fallback', { id, props });
        }
        const crit = (props.critical_count as number) ?? 0;
        const warn = (props.warning_count as number) ?? 0;
        const html = `
          <div class="dashboard-map__popup">
            <div class="dashboard-map__popup-name">${escapeHtml(name)}</div>
            ${crit + warn > 0
              ? `<div class="dashboard-map__popup-alarms">${crit} critical · ${warn} warning</div>`
              : `<div class="dashboard-map__popup-ok">No active alarms</div>`
            }
          </div>`;
        if (!popupRef.current) {
          popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 16, className: 'dashboard-map-popup' });
        }
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        popupRef.current.setLngLat(coords).setHTML(html).addTo(map);
      });
      map.on('mouseleave', 'projects-pin', () => {
        setCursor('');
        onHoverChangeRef.current(null);
        popupRef.current?.remove();
      });
      map.on('click', 'projects-pin', (e) => {
        if (placeModeRef.current) return; // place-mode handler below claims this
        const f = e.features?.[0];
        const id = (f?.id ?? f?.properties?.id) as string | undefined;
        if (id) onPinClickRef.current(id);
      });
      map.on('mouseenter', 'projects-cluster', (e) => {
        setCursor('pointer');
        const f = e.features?.[0];
        if (!f) return;
        const count = (f.properties?.point_count as number) ?? 0;
        const critTotal = (f.properties?.critical_total as number) ?? 0;
        const warnTotal = (f.properties?.warning_total as number) ?? 0;
        const alarmLine = critTotal + warnTotal > 0
          ? `<div class="dashboard-map__popup-alarms">${critTotal} critical · ${warnTotal} warning</div>`
          : '<div class="dashboard-map__popup-ok">No active alarms</div>';
        const html = `
          <div class="dashboard-map__popup">
            <div class="dashboard-map__popup-name">${count} project${count === 1 ? '' : 's'} in this area</div>
            <div class="dashboard-map__popup-hint">Click to zoom in</div>
            ${alarmLine}
          </div>`;
        if (!popupRef.current) {
          popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 22, className: 'dashboard-map-popup' });
        }
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        popupRef.current.setLngLat(coords).setHTML(html).addTo(map);
      });
      map.on('mouseleave', 'projects-cluster', () => {
        setCursor('');
        popupRef.current?.remove();
      });
      map.on('click', 'projects-cluster', (e) => {
        if (placeModeRef.current) return;
        const f = e.features?.[0];
        if (!f) return;
        const clusterId = f.properties?.cluster_id as number | undefined;
        const src = map.getSource('projects') as mapboxgl.GeoJSONSource;
        if (clusterId == null) return;
        src.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || zoom == null) return;
          map.easeTo({
            center: (f.geometry as GeoJSON.Point).coordinates as [number, number],
            zoom: Math.min(zoom + 0.2, 18),
            duration: 600,
          });
        });
      });
      // Place-mode click — bare-canvas click while pinning a site's location.
      map.on('click', (e) => {
        const pm = placeModeRef.current;
        if (!pm) return;
        // Ignore clicks on the cluster/pin layers (their own handlers fired).
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['projects-cluster', 'projects-pin'],
        });
        if (features.length > 0) return;
        onPlaceLocationRef.current(pm.siteId, e.lngLat.lng, e.lngLat.lat);
      });
    });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // Push feature data into the source whenever projects or alarmCounts change.
  // We update latestDataRef on every render so the init effect's style.load
  // handler can seed addSource() with current data even if that handler runs
  // after the data effect (race: data effect fires while style is still
  // loading, registers `once('style.load')`, but style already loaded
  // synchronously from a cached style spec → `once` never re-fires).
  useEffect(() => {
    const fc = toFeatureCollection(projects, alarmCounts);
    latestDataRef.current = fc;

    const map = mapRef.current;
    if (!map) return;

    const src = map.getSource('projects') as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(fc);
    }
    // else: source not yet added (style still loading) — the init effect's
    // style.load handler will pick up latestDataRef.current when it runs.

    // First-time fitBounds — cinematic transition from globe to all pins.
    // Only runs when there's something to fit AND we haven't already framed
    // the camera (saved view or a prior fit). Map style doesn't need to be
    // fully loaded for fitBounds — it queues until ready.
    if (!didInitialFitRef.current && fc.features.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      for (const f of fc.features) {
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        bounds.extend([lng, lat]);
      }
      didInitialFitRef.current = true;
      window.setTimeout(() => {
        if (!mapRef.current) return;
        map.fitBounds(bounds, { padding: 80, maxZoom: 12, duration: 2800, essential: true });
      }, 1200);
    }
  }, [projects, alarmCounts]);

  // Focus change (admin client selection) — re-frame the camera to the new
  // pin set. Declared after the data effect so latestDataRef already holds
  // this render's features. Skips the mount pass (and StrictMode's replay)
  // so the cinematic intro / saved view above still owns the first move.
  const prevFocusKeyRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevFocusKeyRef.current;
    prevFocusKeyRef.current = focusKey;
    if (prev === undefined || prev === focusKey) return;
    const map = mapRef.current;
    if (!map) return;

    if (focusKey === null) {
      const saved = loadSavedView();
      if (saved) {
        map.flyTo({
          center: saved.center,
          zoom: saved.zoom,
          bearing: saved.bearing,
          pitch: saved.pitch,
          duration: 900,
          essential: true,
        });
        return;
      }
    }

    const fc = latestDataRef.current;
    if (fc.features.length === 0) return;
    const bounds = new mapboxgl.LngLatBounds();
    for (const f of fc.features) {
      const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates as [number, number];
      bounds.extend([lng, lat]);
    }
    didInitialFitRef.current = true;
    map.fitBounds(bounds, { padding: 80, maxZoom: 12, duration: 900, essential: true });
  }, [focusKey]);

  // Hover sync: parent's hoveredId drives feature-state.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const prev = hoveredFeatureIdRef.current;
    if (prev && prev !== hoveredId) {
      try { map.setFeatureState({ source: 'projects', id: prev }, { hover: false }); } catch { /* source not ready */ }
    }
    if (hoveredId) {
      try { map.setFeatureState({ source: 'projects', id: hoveredId }, { hover: true }); } catch { /* source not ready */ }
    }
    hoveredFeatureIdRef.current = hoveredId;
  }, [hoveredId]);

  // Place-mode visual: change cursor and add a wrapper class.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = placeMode ? 'crosshair' : '';
  }, [placeMode]);

  const handleSaveView = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    persistSavedView({
      center: [c.lng, c.lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    });
    setHasSavedView(true);
    onViewSaved?.();
  }, [onViewSaved]);

  const handleReturnToView = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const saved = loadSavedView();
    if (!saved) return;
    map.flyTo({
      center: saved.center,
      zoom: saved.zoom,
      bearing: saved.bearing,
      pitch: saved.pitch,
      duration: 1500,
      essential: true,
    });
  }, []);

  const handleClearView = useCallback(() => {
    clearSavedView();
    setHasSavedView(false);
    // Re-fit to all current pins on next data tick.
    didInitialFitRef.current = false;
    const map = mapRef.current;
    if (map) {
      const src = map.getSource('projects') as mapboxgl.GeoJSONSource | undefined;
      // Force the fit-bounds branch to re-run by nudging the source.
      if (src) {
        const data = toFeatureCollection(projects, alarmCounts);
        if (data.features.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          for (const f of data.features) {
            const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates as [number, number];
            bounds.extend([lng, lat]);
          }
          map.fitBounds(bounds, { padding: 80, maxZoom: 12, duration: 600 });
          didInitialFitRef.current = true;
        }
      }
    }
    onViewCleared?.();
  }, [onViewCleared, projects, alarmCounts]);

  return (
    <div className="dashboard-map" ref={containerRef}>
      {placeMode && (
        <div className="dashboard-map__place-banner">
          Click anywhere on the map to pin <strong>{placeMode.siteName}</strong>
        </div>
      )}
      <div className="dashboard-map__view-controls">
        {hasSavedView && (
          <button
            type="button"
            className="dashboard-map__view-btn dashboard-map__view-btn--ghost"
            onClick={handleReturnToView}
            title="Fly back to your saved default view"
            aria-label="Return to saved view"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12l9-9 9 9" />
              <path d="M5 10v10h14V10" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="dashboard-map__view-btn"
          onClick={handleSaveView}
          title={hasSavedView ? 'Update saved view to the current map position' : 'Save current map view as your default'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
          <span>{hasSavedView ? 'Update view' : 'Save view'}</span>
        </button>
        {hasSavedView && (
          <button
            type="button"
            className="dashboard-map__view-btn dashboard-map__view-btn--ghost"
            onClick={handleClearView}
            title="Clear saved view (next load will fit to all projects)"
            aria-label="Clear saved view"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
